import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun } from '@fortawesome/pro-solid-svg-icons/faSun';
import { faMoon } from '@fortawesome/pro-solid-svg-icons/faMoon';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faPenCircle } from '@fortawesome/pro-regular-svg-icons/faPenCircle';
import { faLock } from '@fortawesome/pro-regular-svg-icons/faLock';
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons/faEyeSlash';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { BackgroundCheckRenewModal } from 'us-react-components/dist/BackgroundCheckRenewModal/BackgroundCheckRenewModal';
import {
  formatDateRange,
  getLongDateString,
} from 'us-react-components/dist/utils/date';
import { bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import {
  BackgroundCheckService,
  HiddenJobService,
  Job,
  JobService,
  SitterJobFilter,
  SitterJobFilterService,
} from 'us-web-services';

import { Button, ButtonSize, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { setJobNavState } from './jobNavStorage';

const ALL_VERTICAL_IDS = [
  bookingVerticalType.CHILD_CARE,
  bookingVerticalType.PET_CARE,
  bookingVerticalType.SENIOR_CARE,
  bookingVerticalType.HOUSEHOLD_SERVICES,
];

type TypeCode = 'all' | 'one_time' | 'part_time' | 'full_time';
const TYPE_OPTIONS: Array<{
  code: TypeCode;
  name: string;
  type: string | null;
  availability: string | null;
}> = [
  { code: 'all', name: 'All job types', type: null, availability: null },
  { code: 'one_time', name: 'One-time jobs', type: 'one_time', availability: null },
  { code: 'part_time', name: 'Part-time jobs', type: 'recurring', availability: 'Part Time' },
  { code: 'full_time', name: 'Full-time jobs', type: 'recurring', availability: 'Full Time' },
];

const DISTANCES: Array<{ value: number; name: string }> = [
  { value: 50, name: 'Within 50 miles' },
  { value: 25, name: 'Within 25 miles' },
  { value: 10, name: 'Within 10 miles' },
  { value: 5, name: 'Within 5 miles' },
  { value: 4, name: 'Within 4 miles' },
  { value: 3, name: 'Within 3 miles' },
  { value: 2, name: 'Within 2 miles' },
  { value: 1, name: 'Within 1 mile' },
];

const RATE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any' },
  { value: '15', label: '$15+' },
  { value: '20', label: '$20+' },
  { value: '25', label: '$25+' },
  { value: '30', label: '$30+' },
  { value: '40', label: '$40+' },
];

const VERTICAL_OPTIONS: Array<{ id: number; label: string }> = [
  { id: bookingVerticalType.CHILD_CARE, label: 'Child care' },
  { id: bookingVerticalType.PET_CARE, label: 'Pet care' },
  { id: bookingVerticalType.SENIOR_CARE, label: 'Senior care' },
  { id: bookingVerticalType.HOUSEHOLD_SERVICES, label: 'Household services' },
];

const SORT_OPTIONS: Array<{ sortBy: string; name: string; title: string }> = [
  { sortBy: 'distance', name: 'Closest distance', title: 'Sorted by closest distance' },
  { sortBy: 'updated', name: 'Most recent posts', title: 'Sorted by most recent posts' },
  { sortBy: 'start', name: 'Soonest start date', title: 'Sorted by soonest start date' },
  { sortBy: 'hourlyRate', name: 'Highest rate', title: 'Sorted by highest rate' },
];

type ListParam = 'available' | 'interested' | 'closed' | 'hidden';

const LIST_TABS: Array<{
  value: ListParam;
  labelKey: string;
  icon: typeof faCircleCheck;
}> = [
  { value: 'available', labelKey: 'pages.jobBoard.tabs.available', icon: faCircleCheck },
  { value: 'interested', labelKey: 'pages.jobBoard.tabs.interested', icon: faPenCircle },
  { value: 'closed', labelKey: 'pages.jobBoard.tabs.closed', icon: faLock },
  { value: 'hidden', labelKey: 'pages.jobBoard.tabs.hidden', icon: faEyeSlash },
];

const getRate = (job: Job): string => {
  const booking = job.booking as Job['booking'] & { rate?: number };
  if (!booking?.rate) return 'Rate dependent on experience';
  return `$${booking.rate}/hr`;
};

const getPaymentVia = (job: Job): string => {
  const pt = job.booking?.paymentType;
  if (!pt) return '';
  return pt === 'credit' ? 'via credit card' : `via ${pt}`;
};

// Mirror Angular open-job-card#getChildrenLine.
const getCountLine = (booking?: Job['booking']): string => {
  if (!booking?.numberOfChildren) return '';
  const n = booking.numberOfChildren;
  switch (booking.typeId) {
    case bookingVerticalType.CHILD_CARE:
      return `${n} child${n === 1 ? '' : 'ren'}${
        booking.kidsAges ? ` (${booking.kidsAges})` : ''
      }`;
    case bookingVerticalType.PET_CARE:
      return `${n} ${n === 1 ? 'pet' : 'pets'}`;
    case bookingVerticalType.SENIOR_CARE:
      return `${n} ${n === 1 ? 'senior' : 'seniors'}`;
    case bookingVerticalType.HOUSEHOLD_SERVICES:
      return '';
    default:
      return `${n} child${n === 1 ? '' : 'ren'}`;
  }
};

const VERTICAL_META: Record<number, { label: string; accent: string; badge: string }> = {
  [bookingVerticalType.CHILD_CARE]: {
    label: 'Child care',
    accent: 'border-l-sapphire',
    badge: 'bg-sapphire-50 text-sapphire',
  },
  [bookingVerticalType.PET_CARE]: {
    label: 'Pet care',
    accent: 'border-l-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700',
  },
  [bookingVerticalType.SENIOR_CARE]: {
    label: 'Senior care',
    accent: 'border-l-honey-500',
    badge: 'bg-honey-50 text-honey-800',
  },
  [bookingVerticalType.HOUSEHOLD_SERVICES]: {
    label: 'Household',
    accent: 'border-l-neutral-400',
    badge: 'bg-neutral-100 text-neutral-700',
  },
};

interface Address {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  latitude?: number;
  longitude?: number;
}

export const JobBoard = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores, services: { tracker } } = useDependencies();
  const sitter = stores.sitter.sitter as (typeof stores.sitter.sitter) & {
    address?: Address;
    hidden?: boolean;
  };

  const initialVerticals = ((): Set<number> => {
    const raw = searchParams.get('verticalTypeIds');
    if (raw) {
      const ids = raw.split(',').map(Number).filter((n) => !Number.isNaN(n));
      if (ids.length > 0) return new Set(ids);
    }
    return new Set(ALL_VERTICAL_IDS);
  })();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [totalJobs, setTotalJobs] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isMoreLoaded, setIsMoreLoaded] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [listParam, setListParam] = useState<ListParam>(
    (searchParams.get('list') as ListParam) ?? 'available',
  );
  const [sort, setSort] = useState<string>(searchParams.get('sort') ?? 'distance');
  const [typeCode, setTypeCode] = useState<TypeCode>(
    (searchParams.get('typeCode') as TypeCode) ?? 'all',
  );
  const [distance, setDistance] = useState<number>(
    Number(searchParams.get('distance')) || 50,
  );
  const [minRate, setMinRate] = useState<string>(
    searchParams.get('rateGreaterThan') ?? '',
  );
  const [startDate, setStartDate] = useState<string>(searchParams.get('start') ?? '');
  const [verticals, setVerticals] = useState<Set<number>>(initialVerticals);
  const [address, setAddress] = useState<Address>({
    street: sitter?.address?.street,
    city: sitter?.address?.city,
    state: sitter?.address?.state,
    zip: sitter?.address?.zip,
    latitude: sitter?.address?.latitude,
    longitude: sitter?.address?.longitude,
  });
  const [bgCheckRenewOpen, setBgCheckRenewOpen] = useState(false);
  const [bgCheckUrl, setBgCheckUrl] = useState<string | undefined>();
  const [bgCheckExpiresLabel, setBgCheckExpiresLabel] = useState<string | undefined>();
  const [savedFilterId, setSavedFilterId] = useState<number | null>(null);
  const [savedFilterLoaded, setSavedFilterLoaded] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Job Board';
  }, []);

  // Load the caregiver's saved job-board filter (Angular: SitterJobFilter.webservice.get
  // on mount). URL params win over saved values so deep links remain authoritative.
  useEffect(() => {
    if (!sitter?.id) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterJobFilterService.getByFilter({
          params: { sitterId: sitter.id },
        });
        if (cancelled) return;
        const saved = resp.data?.data?.[0];
        if (saved) {
          if (saved.id != null) setSavedFilterId(saved.id);
          if (!searchParams.get('distance') && saved.distance != null) {
            setDistance(saved.distance);
          }
          if (!searchParams.get('sort') && saved.sort) setSort(saved.sort);
          if (!searchParams.get('rateGreaterThan') && saved.rate != null) {
            setMinRate(String(saved.rate));
          }
          if (
            !searchParams.get('verticalTypeIds')
            && saved.verticalTypeIds
            && saved.verticalTypeIds.length > 0
          ) {
            setVerticals(new Set(saved.verticalTypeIds));
          }
          if (!searchParams.get('typeCode')) {
            const match = TYPE_OPTIONS.find(
              (o) => o.type === (saved.type ?? null)
                && o.availability === (saved.availability ?? null),
            );
            if (match) setTypeCode(match.code);
          }
          setAddress((prev) => ({
            ...prev,
            street: prev.street ?? saved.street,
            city: prev.city ?? saved.city,
            state: prev.state ?? saved.state,
            zip: prev.zip ?? saved.zip,
            latitude: prev.latitude ?? saved.latitude,
            longitude: prev.longitude ?? saved.longitude,
          }));
        }
      } catch (err) {
        console.warn('[JobBoard] saved filter load failed', err);
      } finally {
        if (!cancelled) setSavedFilterLoaded(true);
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
    // Only re-run when the sitter identity changes; searchParams reads inside are
    // intentional one-shot defaults at load time.

  }, [sitter?.id]);

  // Keep the address in sync with sitter profile when it first loads.
  useEffect(() => {
    if (!sitter?.address) return;
    setAddress((prev) => ({
      ...prev,
      street: prev.street ?? sitter.address?.street,
      city: prev.city ?? sitter.address?.city,
      state: prev.state ?? sitter.address?.state,
      zip: prev.zip ?? sitter.address?.zip,
      latitude: prev.latitude ?? sitter.address?.latitude,
      longitude: prev.longitude ?? sitter.address?.longitude,
    }));
  }, [sitter?.address]);

  // Background-check renewal nudge.
  // Angular source: apps/angular/src/js/ng/sitter/job/board.js#checkBackgroundCheck
  useEffect(() => {
    if (!sitter?.id || !sitter.active || sitter.hidden) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await BackgroundCheckService.findByFilter({ params: { sitterId: sitter.id } });
        const bg = ((resp.data?.data as Array<{
          expiration?: string;
          status?: { label?: string };
          url?: string;
        }>) ?? [])[0];
        if (!bg?.status?.label) return;
        if (['Failed', 'Submitted'].includes(bg.status.label)) return;
        if (!bg.expiration) return;
        const hiddenDate = new Date(bg.expiration);
        hiddenDate.setDate(hiddenDate.getDate() + 30);
        const daysUntilHidden = Math.floor(
          (hiddenDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
        );
        if (daysUntilHidden >= 0 && daysUntilHidden <= 30) {
          setBgCheckUrl(bg.url ?? '/app/finish-background-check');
          setBgCheckExpiresLabel(getLongDateString(hiddenDate));
          setBgCheckRenewOpen(true);
        }
      } catch {
        /* non-fatal */
      }
    };
    void load();

  }, [sitter?.id, sitter?.active]);

  const typeOption = useMemo(
    () => TYPE_OPTIONS.find((o) => o.code === typeCode) ?? TYPE_OPTIONS[0],
    [typeCode],
  );

  const sortTitle = useMemo(
    () => SORT_OPTIONS.find((o) => o.sortBy === sort)?.title ?? 'Sorted by closest distance',
    [sort],
  );

  const getListApiParams = (list: ListParam): Record<string, unknown> => {
    switch (list) {
      case 'interested':
        return { interested: true, hidden: false };
      case 'hidden':
        return { hidden: true };
      case 'closed':
        return { interested: true, hidden: false, status: 'closed' };
      default:
        return { hidden: false, interested: false };
    }
  };

  const fetchJobs = async (pageArg: number, append: boolean): Promise<void> => {
    if (!sitter?.id) return;
    if (append) setIsMoreLoaded(false);
    else setIsLoading(true);

    const listApiParams = getListApiParams(listParam);

    const params: Record<string, unknown> = {
      sitterId: sitter.id,
      page: pageArg,
      limit: 10,
      sort,
      distance,
      verticalTypeIds: [...verticals],
      ...listApiParams,
    };
    if (typeOption.type) params.type = typeOption.type;
    if (typeOption.availability) params.availability = typeOption.availability;
    if (minRate) {
      params.rateGreaterThan = minRate;
      params.rate = minRate;
    }
    if (startDate) {
      params.q = `start.gt('${new Date(startDate).toISOString()}')`;
    }
    if (address.city) params.city = address.city;
    if (address.state) params.state = address.state;
    if (address.zip) params.zip = address.zip;
    if (address.street) params.street = address.street;
    if (address.latitude != null) params.latitude = address.latitude;
    if (address.longitude != null) params.longitude = address.longitude;

    if (!append) setLoadError(false);
    try {
      const resp = (await JobService.getByFilter({ params })).data;
      const data = resp?.data ?? [];
      setJobs((prev) => (append ? [...prev, ...data] : data));
      const meta = resp?.meta;
      setHasMore(!!meta && !meta.lastPage);
      setTotalJobs(meta?.size ?? null);
    } catch {
      if (!append) {
        setJobs([]);
        setLoadError(true);
        setTotalJobs(0);
      }
    } finally {
      setIsLoading(false);
      setIsMoreLoaded(true);
    }
  };

  useEffect(() => {
    setPage(1);
    void fetchJobs(1, false);

  }, [
    sitter?.id,
    listParam,
    sort,
    typeCode,
    distance,
    minRate,
    startDate,
    verticals,
    address.city,
    address.state,
    address.zip,
    address.latitude,
    address.longitude,
  ]);

  // Persist filter changes back to /v2/sitter-job-filters so the next visit
  // restores them. Debounced to avoid hammering the API while the user toggles
  // multiple checkboxes in quick succession.
  useEffect(() => {
    if (!sitter?.id || !savedFilterLoaded) return undefined;
    const handle = window.setTimeout(() => {
      const dto: SitterJobFilter = {
        sitterId: sitter.id,
        type: typeOption.type,
        availability: typeOption.availability,
        distance,
        sort,
        verticalTypeIds: [...verticals],
        rate: minRate ? Number(minRate) : undefined,
        street: address.street,
        city: address.city,
        state: address.state,
        zip: address.zip,
        latitude: address.latitude,
        longitude: address.longitude,
      };
      const persist = async (): Promise<void> => {
        try {
          if (savedFilterId != null) {
            await SitterJobFilterService.update(savedFilterId, dto);
          } else {
            const resp = await SitterJobFilterService.create(dto);
            const created = resp.data?.data;
            if (created?.id != null) setSavedFilterId(created.id);
          }
        } catch (err) {
          console.warn('[JobBoard] saved filter persist failed', err);
        }
      };
      void persist();
    }, 600);
    return (): void => window.clearTimeout(handle);

  }, [
    savedFilterLoaded,
    sitter?.id,
    sort,
    typeCode,
    distance,
    minRate,
    verticals,
    address.city,
    address.state,
    address.zip,
    address.street,
    address.latitude,
    address.longitude,
  ]);

  const updateParam = (key: string, value: string | null): void => {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        if (value == null || value === '') next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  const setListParamValue = (val: ListParam): void => {
    setListParam(val);
    updateParam('list', val === 'available' ? null : val);
  };

  const setSortValue = (val: string): void => {
    setSort(val);
    updateParam('sort', val === 'distance' ? null : val);
  };

  const setTypeValue = (val: TypeCode): void => {
    setTypeCode(val);
    updateParam('typeCode', val === 'all' ? null : val);
  };

  const setDistanceValue = (val: number): void => {
    setDistance(val);
    updateParam('distance', val === 50 ? null : String(val));
  };

  const setMinRateValue = (val: string): void => {
    setMinRate(val);
    updateParam('rateGreaterThan', val);
  };

  const setStartDateValue = (val: string): void => {
    setStartDate(val);
    updateParam('start', val);
  };

  const toggleVertical = (id: number): void => {
    setVerticals((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      const asList = [...next].sort((a, b) => a - b);
      updateParam(
        'verticalTypeIds',
        asList.length === ALL_VERTICAL_IDS.length ? null : asList.join(','),
      );
      return next;
    });
  };

  const applyAddress = (nextAddress: Address): void => {
    setAddress(nextAddress);
  };

  const loadMore = (): void => {
    const next = page + 1;
    setPage(next);
    void fetchJobs(next, true);
  };

  const hideJob = async (jobId: number): Promise<void> => {
    if (!sitter?.id) return;
    try {
      await HiddenJobService.hide(sitter.id, jobId);
      tracker.track('job-action', { action: 'hide-job', value: jobId });
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (err) {
      console.warn('[JobBoard] hide job failed', err);
    }
  };

  const unhideJob = async (jobId: number): Promise<void> => {
    if (!sitter?.id) return;
    try {
      await HiddenJobService.unhide(sitter.id, jobId);
      tracker.track('job-action', { action: 'unhide-job', value: jobId });
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (err) {
      console.warn('[JobBoard] unhide job failed', err);
    }
  };

  // Hide button shows when viewing available (not hidden, not applied) jobs.
  const showHideBtn = listParam === 'available';

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-5'>{t('pages.jobBoard.heading')}</h1>

      <div className='flex flex-wrap gap-2 mb-6'>
        {LIST_TABS.map((tab) => {
          const isActive = listParam === tab.value;
          return (
            <Button
              key={tab.value}
              buttonType={isActive ? ButtonType.primary : ButtonType.tertiary}
              buttonSize={ButtonSize.small}
              onClick={(): void => setListParamValue(tab.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border font-medium text-sm transition-colors ${
                isActive
                  ? 'border-sapphire !bg-sapphire focus:!bg-sapphire hover:!bg-sapphire-700'
                  : 'bg-white text-neutral-700 border-neutral-300 hover:border-sapphire hover:text-sapphire'
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} />
              {t(tab.labelKey)}
            </Button>
          );
        })}
      </div>

      {/* Top row: type + distance selects */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 max-w-xl'>
        <div>
          <label htmlFor='typeSelect' className='block font-semibold mb-1'>
            Type:
          </label>
          <select
            id='typeSelect'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            value={typeCode}
            onChange={(e): void => setTypeValue(e.target.value as TypeCode)}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.code} value={o.code}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor='distanceSelect' className='block font-semibold mb-1'>
            Distance:
          </label>
          <select
            id='distanceSelect'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            value={distance}
            onChange={(e): void => setDistanceValue(Number(e.target.value))}
          >
            {DISTANCES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
        {/* Sidebar filter */}
        <aside className='md:col-span-1'>
          <div className='us-card rounded p-4'>
            <h3 className='font-semibold mb-3'>More Filters</h3>

            <div className='mb-4'>
              <h4 className='font-semibold text-sm mb-2'>Job type</h4>
              <div className='space-y-1'>
                {VERTICAL_OPTIONS.map((v) => (
                  <label key={v.id} className='flex items-center gap-2 text-sm'>
                    <input
                      type='checkbox'
                      checked={verticals.has(v.id)}
                      onChange={(): void => toggleVertical(v.id)}
                    />
                    {v.label}
                  </label>
                ))}
              </div>
            </div>

            <div className='mb-4'>
              <h4 className='font-semibold text-sm mb-2'>Location</h4>
              <input
                type='text'
                aria-label='Street'
                placeholder='Street'
                className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm mb-2'
                value={address.street ?? ''}
                onChange={(e): void =>
                  setAddress((prev) => ({ ...prev, street: e.target.value }))
                }
              />
              <div className='grid grid-cols-5 gap-1 mb-2'>
                <input
                  type='text'
                  aria-label='City'
                  placeholder='City'
                  className='col-span-3 form-control border border-neutral-300 rounded px-2 py-1 text-sm'
                  value={address.city ?? ''}
                  onChange={(e): void =>
                    setAddress((prev) => ({ ...prev, city: e.target.value }))
                  }
                />
                <input
                  type='text'
                  aria-label='State'
                  placeholder='State'
                  maxLength={2}
                  className='col-span-2 form-control border border-neutral-300 rounded px-2 py-1 text-sm uppercase'
                  value={address.state ?? ''}
                  onChange={(e): void =>
                    setAddress((prev) => ({
                      ...prev,
                      state: e.target.value.toUpperCase(),
                    }))
                  }
                />
              </div>
              <input
                type='tel'
                aria-label='Zip code'
                placeholder='Zip code'
                maxLength={5}
                className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm mb-2'
                value={address.zip ?? ''}
                onChange={(e): void =>
                  setAddress((prev) => ({ ...prev, zip: e.target.value }))
                }
              />
              <Button
                id='set-location'
                buttonType={ButtonType.primary}
                className='btn-block w-full text-sm py-1'
                onClick={(): void => applyAddress(address)}
              >
                Set Location
              </Button>
            </div>

            <div className='mb-4'>
              <h4 className='font-semibold text-sm mb-2'>Hourly Rate Minimum</h4>
              <select
                id='rate-select'
                className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm'
                value={minRate}
                onChange={(e): void => setMinRateValue(e.target.value)}
              >
                {RATE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div className='mb-4'>
              <h4 className='font-semibold text-sm mb-2'>
                <label htmlFor='startDatePicker'>Starting</label>
              </h4>
              <input
                id='startDatePicker'
                type='date'
                className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm'
                value={startDate}
                onChange={(e): void => setStartDateValue(e.target.value)}
              />
              {startDate && (
                <Button
                  buttonType={ButtonType.tertiary}
                  className='w-full text-xs mt-1'
                  onClick={(): void => setStartDateValue('')}
                >
                  Clear date
                </Button>
              )}
            </div>

            <div className='border-t pt-3 text-xs'>
              <h4 className='font-semibold text-sm mb-2'>Key</h4>
              <div className='flex items-center gap-2 mb-1'>
                <FontAwesomeIcon icon={faSun} className='text-amber-500' />
                <span>Day-time job</span>
              </div>
              <div className='flex items-center gap-2'>
                <FontAwesomeIcon icon={faMoon} className='text-sapphire' />
                <span>Night-time job</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div className='md:col-span-3'>
          {/* Results header: count + sort dropdown — mirrors Angular job-search-results-header */}
          {!isLoading && jobs.length > 0 && (
            <div className='flex items-center justify-between mb-4 gap-3'>
              <h3 className='text-base font-semibold m-0'>
                {totalJobs != null && (
                  <span className='mr-1'>
                    {totalJobs} {totalJobs === 1 ? 'Job' : 'Jobs'}
                  </span>
                )}
                <span className='font-normal text-neutral-600'>{sortTitle}</span>
              </h3>
              <select
                aria-label='Sort jobs'
                className='form-control border border-neutral-300 rounded pl-3 pr-8 py-2 text-sm font-medium cursor-pointer'
                value={sort}
                onChange={(e): void => setSortValue(e.target.value)}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.sortBy} value={o.sortBy}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isLoading && (
            <div className='text-center pt-4 text-neutral-600'>
              <i className='fa-solid fa-spinner fa-spin mr-2' />
              <span>{t('pages.jobBoard.loading')}</span>
            </div>
          )}

          {!isLoading && loadError && jobs.length === 0 && (
            <div className='us-card rounded p-6'>
              <h4 className='mb-2 font-semibold'>{t('common.errors.loadFailedTitle')}</h4>
              <p className='mb-3'>{t('common.errors.loadFailedBody')}</p>
              <Button
                buttonType={ButtonType.primary}
                onClick={(): void => {
                  setPage(1);
                  void fetchJobs(1, false);
                }}
              >
                {t('common.errors.retry')}
              </Button>
            </div>
          )}

          {!isLoading && !loadError && jobs.length === 0 && ((): JSX.Element => {
            const hasFilters =
              typeCode !== 'all' ||
              distance !== 50 ||
              !!minRate ||
              !!startDate ||
              verticals.size !== ALL_VERTICAL_IDS.length;
            if (hasFilters) {
              return (
                <div className='us-card rounded p-6'>
                  <h4 className='mb-2 font-semibold'>{t('pages.jobBoard.noMatchFilters')}</h4>
                  <span>{t('pages.jobBoard.tryDifferent')}</span>
                </div>
              );
            }
            return (
              <div className='us-card rounded p-6'>
                <h4 className='mb-2 font-semibold'>Want to see more jobs?</h4>
                <p className='mb-3'>New jobs are posted all the time.</p>
                <Button
                  buttonType={ButtonType.primary}
                  onClick={(): void => {
                    setPage(1);
                    void fetchJobs(1, false);
                  }}
                >
                  Refresh the job board
                </Button>
              </div>
            );
          })()}

          {!isLoading &&
            jobs.map((job) => {
              const booking = job.booking;
              const parent = booking?.parent;
              const meta = booking?.typeId ? VERTICAL_META[booking.typeId] : undefined;
              const countLine = getCountLine(booking);
              const paymentVia = getPaymentVia(job);
              const locationBits = [booking?.address?.city, booking?.address?.state]
                .filter(Boolean)
                .join(', ');
              const rate = getRate(job);
              const hasApplied = !!job.sitterInterest;
              const isClosed = job.status?.includes('closed') ?? false;

              return (
                <div
                  key={job.id}
                  className={`us-card rounded mb-3 border-l-4 ${
                    meta?.accent ?? 'border-l-neutral-300'
                  }`}
                >
                  <div className='flex flex-col md:flex-row'>
                    {/* Left: job details */}
                    <div
                      role='button'
                      tabIndex={0}
                      className='flex-1 p-4 cursor-pointer hover:bg-neutral-50'
                      onClick={(): void => {
                        setJobNavState({
                          ids: jobs.map((j) => j.id).filter((id): id is number => id != null),
                          searchUrl: `/job-board${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
                          searchText: 'Return to search results',
                        });
                        navigate(`/job/${job.id}`);
                      }}
                      onKeyDown={(e): void => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setJobNavState({
                            ids: jobs.map((j) => j.id).filter((id): id is number => id != null),
                            searchUrl: `/job-board${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
                            searchText: 'Return to search results',
                          });
                          navigate(`/job/${job.id}`);
                        }
                      }}
                    >
                      <div className='flex items-start gap-3'>
                        {parent?.pictureUrl && (
                          <img
                            src={parent.pictureUrl}
                            alt={parent.firstName ?? ''}
                            className='w-20 h-20 rounded-full object-cover flex-shrink-0'
                          />
                        )}
                        <div className='flex-1 min-w-0'>
                          <div className='flex flex-wrap items-center gap-2 mb-1'>
                            {meta && (
                              <span
                                className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${meta.badge}`}
                              >
                                {meta.label}
                              </span>
                            )}
                            <span className='text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700'>
                              {job.recurring ? 'Recurring' : 'One-time'}
                            </span>
                            {typeof job.distance === 'number' && (
                              <span className='text-xs text-neutral-500'>
                                {job.distance.toFixed(1)} mi
                              </span>
                            )}
                          </div>
                          <div className='font-semibold'>
                            {job.recurring
                              ? `${job.availability ?? ''}, Starting ${formatDateRange(booking?.start)}`
                              : formatDateRange(booking?.start, booking?.end)}
                          </div>
                          {countLine && (
                            <div className='text-sm text-neutral-700'>{countLine}</div>
                          )}
                          {locationBits && (
                            <div className='text-sm text-neutral-600'>{locationBits}</div>
                          )}
                          {((booking as { message?: string })?.message || booking?.description) && (
                            <div className='text-sm mt-2 line-clamp-2 text-neutral-700'>
                              {(booking as { message?: string })?.message || booking?.description}
                            </div>
                          )}
                          <Button
                            id={`full-details-${job.id}`}
                            buttonType={ButtonType.tertiary}
                            className='inline-block mt-2 text-sm font-semibold text-sapphire hover:underline bg-transparent border-0 p-0'
                            onClick={(e): void => {
                              e.stopPropagation();
                              setJobNavState({
                                ids: jobs
                                  .map((j) => j.id)
                                  .filter((id): id is number => id != null),
                                searchUrl: `/job-board${
                                  searchParams.toString() ? `?${searchParams.toString()}` : ''
                                }`,
                                searchText: 'Return to search results',
                              });
                              navigate(`/job/${job.id}`);
                            }}
                          >
                            Full Details
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Right: rate + CTAs — mirrors Angular job-summary col-md-4 */}
                    <div className='md:w-48 p-4 border-t md:border-t-0 md:border-l border-neutral-200 flex flex-col gap-3'>
                      {/* Rate (prominent) */}
                      <div>
                        <div className='font-semibold text-base'>{rate}</div>
                        {paymentVia && (
                          <div className='text-xs text-neutral-600'>{paymentVia}</div>
                        )}
                        {job.minimumHours != null && (
                          <div className='text-sm'>
                            Guaranteed {job.minimumHours} hrs/week
                          </div>
                        )}
                      </div>

                      {/* Less than 10 applicants badge — mirrors Angular applicant-count-summary */}
                      {job.lowNumberOfApplicants && (
                        <div className='flex items-center gap-1 text-xs font-semibold text-blue-600'>
                          <FontAwesomeIcon icon={faStar} className='text-blue-500' />
                          Less than 10 applicants
                        </div>
                      )}

                      {/* Apply / status CTA */}
                      {!isClosed && !hasApplied && (
                        <Button
                          buttonType={ButtonType.primary}
                          className='text-sm w-full'
                          onClick={(e): void => {
                            e.stopPropagation();
                            setJobNavState({
                              ids: jobs.map((j) => j.id).filter((id): id is number => id != null),
                              searchUrl: `/job-board${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
                              searchText: 'Return to search results',
                            });
                            navigate(`/job/${job.id}`);
                          }}
                        >
                          <FontAwesomeIcon icon={faPenCircle} className='mr-1' />
                          Apply
                        </Button>
                      )}
                      {!isClosed && hasApplied && (
                        <div className='text-sm text-sapphire font-medium'>
                          <FontAwesomeIcon icon={faCircleCheck} className='mr-1' />
                          Applied
                        </div>
                      )}
                      {isClosed && (
                        <div className='text-sm text-neutral-500 font-medium'>
                          This job is closed
                        </div>
                      )}

                      {/* Hide / Unhide job */}
                      {showHideBtn && !hasApplied && (
                        <Button
                          buttonType={ButtonType.tertiary}
                          className='text-sm w-full'
                          onClick={(e): void => {
                            e.stopPropagation();
                            void hideJob(job.id!);
                          }}
                        >
                          <FontAwesomeIcon icon={faEyeSlash} className='mr-1' />
                          Hide job
                        </Button>
                      )}
                      {listParam === 'hidden' && !hasApplied && (
                        <Button
                          buttonType={ButtonType.primary}
                          className='text-sm w-full'
                          onClick={(e): void => {
                            e.stopPropagation();
                            void unhideJob(job.id!);
                          }}
                        >
                          <FontAwesomeIcon icon={faEye} className='mr-1' />
                          Unhide job
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

          {!isLoading && hasMore && (
            <div className='text-center my-10'>
              <Button
                buttonType={ButtonType.primary}
                className='px-8'
                onClick={loadMore}
                disabled={!isMoreLoaded}
              >
                {isMoreLoaded ? (
                  t('pages.jobBoard.moreJobs')
                ) : (
                  <>
                    <i className='fa-solid fa-spinner fa-spin mr-2' />
                    {t('pages.jobBoard.loadingMore')}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Clear-filters hint when at end of hidden/interested tabs with filters active */}
          {!isLoading && !hasMore && listParam === 'hidden' && (
            <p className='text-center mt-4 text-sm text-neutral-600'>
              <Button
                buttonType={ButtonType.tertiary}
                className='text-sapphire underline mr-1'
                onClick={(): void => setListParamValue('available')}
              >
                Clear filters
              </Button>
              to see all of your recent hidden jobs
            </p>
          )}
          {!isLoading && !hasMore && listParam === 'interested' && (
            <p className='text-center mt-4 text-sm text-neutral-600'>
              <Button
                buttonType={ButtonType.tertiary}
                className='text-sapphire underline mr-1'
                onClick={(): void => setListParamValue('available')}
              >
                Clear filters
              </Button>
              to see all of the recent jobs you&apos;ve applied to
            </p>
          )}
        </div>
      </div>

      <BackgroundCheckRenewModal
        isOpen={bgCheckRenewOpen}
        onClose={(): void => setBgCheckRenewOpen(false)}
        onRenew={(): void => {
          setBgCheckRenewOpen(false);
          if (bgCheckUrl) {
            window.open(bgCheckUrl, '_blank', 'noopener,noreferrer');
          }
        }}
        expiresOn={bgCheckExpiresLabel}
      />
    </div>
  );
});
